import { ComponentProps, FC, useMemo } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Command } from "@vendor/cmdk/cmdk";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import styles from "./CommandItem.module.scss";

export interface CommandItemProps extends ComponentProps<typeof Command.Item> {
    /** Hotkey string shown on the row, e.g. `g>t`. */
    shortcut?: string;
    disabledTooltip?: string;
    /** Hint for an enabled row, e.g. that selecting it opens an upsell. */
    tooltip?: string;
}

/**
 * A cmdk row takes `disabled` as `isDisabled` here: the row is a div marked
 * `aria-disabled` that still receives hover, and a top-level `disabled` prop
 * would make `Tooltip` wrap it in a span that breaks the list layout.
 */
const CommandItemRow = ({
    isDisabled,
    ...props
}: Omit<ComponentProps<typeof Command.Item>, "disabled"> & {
    isDisabled?: boolean;
}) => <Command.Item {...props} disabled={isDisabled} />;

export const CommandItem: FC<CommandItemProps> = ({
    children,
    className,
    shortcut,
    disabledTooltip,
    tooltip,
    disabled,
    ...commandItemProps
}) => {
    const keywordsWithShortcuts = useMemo(() => {
        const result = [...(commandItemProps.keywords ?? [])];

        if (shortcut) {
            const shortcutSteps = shortcut
                .split(">")
                .map((step) => step.trim());
            result.push(...shortcutSteps);
            result.push(`shortcut:${shortcutSteps.join("").toLowerCase()}`);
        }

        return result;
    }, [commandItemProps.keywords, shortcut]);

    const item = (
        <CommandItemRow
            className={className}
            {...commandItemProps}
            isDisabled={disabled}
            keywords={keywordsWithShortcuts}
        >
            {children}

            {shortcut ? (
                <Shortcut keys={shortcut} className={styles.itemShortcut} />
            ) : null}

            <Shortcut keys="enter" className={styles.confirmShortcut} />
        </CommandItemRow>
    );

    const tooltipContent = disabled ? disabledTooltip : tooltip;

    if (tooltipContent) {
        return (
            <Tooltip content={tooltipContent} side="left">
                {item}
            </Tooltip>
        );
    }

    return item;
};
